/**
 * <s-product-gallery> — one main product image at a time with a thumbnail strip.
 *
 * The main track is a native scroll-snap container, so swiping works without JS.
 * This component wires the thumbnails (jump to that slide), the thumbnail arrows
 * (step one slide back/forward) and keeps aria-current on the active thumbnail.
 *
 * Expected DOM (rendered by templates/product.liquid):
 *   <s-product-gallery>
 *     <div data-gallery-track> <div data-gallery-slide>…</div>… </div>
 *     <button data-gallery-prev> <div data-gallery-thumbs> <button data-gallery-thumb="0">… </div> <button data-gallery-next>
 *   </s-product-gallery>
 */
class SProductGallery extends HTMLElement {
  #ac;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbStrip = this.querySelector('[data-gallery-thumbs]');
    if (!this.track) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('click', this.#onClick, opts);
    this.track.addEventListener('scroll', this.#onScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get slides() {
    return [...this.track.querySelectorAll('[data-gallery-slide]')];
  }

  get thumbs() {
    return [...this.querySelectorAll('[data-gallery-thumb]')];
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      this.goTo(Number(thumb.dataset.galleryThumb));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) this.goTo(this.#index - 1);
    if (event.target.closest('[data-gallery-next]')) this.goTo(this.#index + 1);
  };

  #onScroll = () => {
    const width = this.track.clientWidth || 1;
    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    if (index !== this.#index) {
      this.#index = index;
      this.#update();
    }
  };

  goTo(index) {
    const slides = this.slides;
    if (!slides.length) return;
    const next = Math.max(0, Math.min(slides.length - 1, index));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: slides[next].offsetLeft - this.track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#index = next;
    this.#update();
  }

  #update() {
    const thumbs = this.thumbs;
    thumbs.forEach((thumb, i) => {
      if (i === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        if (this.thumbStrip) {
          const left = thumb.offsetLeft - this.thumbStrip.offsetLeft;
          const right = left + thumb.offsetWidth;
          const view = this.thumbStrip.scrollLeft;
          if (left < view || right > view + this.thumbStrip.clientWidth) {
            this.thumbStrip.scrollTo({ left: left, behavior: 'smooth' });
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.#index <= 0;
    if (next) next.disabled = this.#index >= this.slides.length - 1;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
